import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import smoothscroll from 'smoothscroll-polyfill';
import classes from './Layout.module.css';
import LayoutNavBar from './LayoutNavBar';
import ServersModal from 'components/Modals/ServersModal/ServersModal';
import Banner from 'components/UI/Banner/Banner';
import Footer from 'components/UI/Footer/Footer';
import Modal from 'components/UI/Modal/Modal';
import Drafts from 'containers/Log/Drafts/Drafts';
import PostForm from 'containers/Posts/PostForm/PostForm';
import ProfileForm from 'containers/ProfileForm/ProfileForm';
import * as actions from 'store/actions';
import { setSelectedServer } from 'store/thunks';
// kick off the polyfill!
smoothscroll.polyfill();

const Layout = ({ children }) => {
  const [showServerListModal, setShowServerListModal] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const onSetSelectedServer = useCallback((server) => dispatch(setSelectedServer(server)), [dispatch]);
  const onClosePostForm = useCallback(() => dispatch(actions.closePostForm()), [dispatch]);
  const onUpdateScrollToTopOfChild = useCallback((scrollToTop) => dispatch(actions.updateScrollToTopOfChild(scrollToTop)), [dispatch]);
  const onToggleGiftsDraftsModal = useCallback(() => dispatch(actions.toggleGiftsDraftsModal()), [dispatch]);
  const onClearAppError = useCallback(() => dispatch(actions.clearAppError()), [dispatch]);

  const { selectedServer, servers, errorTitle: appErrorTitle, errorMessage: appErrorMessage } = useSelector((state) => state.app);
  const { redirectPath, showPostForm } = useSelector((state) => state.post);
  const { isAuthenticated, showProfileForm } = useSelector((state) => state.auth);
  const showGiftsDraftsModal = useSelector((state) => state.logs.showGiftsDraftsModal);

  const handleClickOutside = useCallback(
    (event) => {
      const accountNavContainer = document.getElementById('accountDropdownContainer');
      if (showAccountDropdown && accountNavContainer && accountNavContainer !== event.target && !accountNavContainer.contains(event.target))
        setShowAccountDropdown(false);
    },
    [showAccountDropdown],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const closePostFormModal = (actionType, isDraft) => {
    if (actionType === 'delete') {
      const pathname = isDraft ? '/life-shared/drafts' : redirectPath.includes('/list') ? '/life-shared/list' : '/life-shared/timeline';
      history.push({
        pathname,
        state: { prevPath: 'post-form' },
      });
    }
    onClosePostForm();
  };

  const toggleServerListModal = useCallback(() => {
    setShowServerListModal((prevState) => !prevState);
  }, []);

  const handleServerSelection = useCallback(
    (server) => {
      onSetSelectedServer(server);
      toggleServerListModal();
    },
    [onSetSelectedServer, toggleServerListModal],
  );

  const handleNavLinkClick = useCallback(
    (pathName) => {
      if (pathName.includes('life-shared')) onUpdateScrollToTopOfChild(true);
      setTimeout(() => {
        setShowAccountDropdown(false);
      }, 1000);
      history.push({
        pathname: pathName,
      });
    },
    [history, onUpdateScrollToTopOfChild],
  );

  let renderedModal = null;
  const isMobile = location.search === '?src=mobileapp';

  if (showGiftsDraftsModal || showPostForm) {
    renderedModal = (
      <Modal
        name={showGiftsDraftsModal ? 'gifts-drafts' : 'post-form'}
        className={showGiftsDraftsModal ? 'GiftsDrafts' : 'PostForm'}
        shortWindow={window.innerHeight <= 730}
        close={showGiftsDraftsModal ? onToggleGiftsDraftsModal : closePostFormModal}
        closeOnOutsideClick={false}
        show
      >
        {showGiftsDraftsModal ? (
          <Drafts close={onToggleGiftsDraftsModal} />
        ) : (
          <PostForm shortWindow={window.innerHeight <= 730} close={onClosePostForm} cancelPost={onClosePostForm} />
        )}
      </Modal>
    );
  } else if (showProfileForm) {
    renderedModal = (
      <Modal show closeOnOutsideClick={false} className="ProfileForm">
        <ProfileForm />
      </Modal>
    );
  } else if (showServerListModal) {
    renderedModal = (
      <Modal close={toggleServerListModal} show>
        <ServersModal servers={servers} selectedServer={selectedServer} selectServer={handleServerSelection} toggle={toggleServerListModal} />
      </Modal>
    );
  }

  const layoutContainerClasses = [classes.Layout];
  // The pages below are shared with iOS and can be accessed whether or not the user is authenticated
  const staticPages = ['/help', '/terms', '/privacy'];
  const appErrorBannerHiddenPages = ['/sign-up', '/sign-in', '/request-password-reset', '/password-reset/', '/verify', '/add-mobile'];

  const showFooter = location.pathname === '/' || location.pathname === '' || location.pathname.includes('/my-riza');
  if (location.pathname.includes('/preview')) {
    layoutContainerClasses.push(classes.PreviewMode);
  } else if (location.pathname.includes('/posts/')) {
    layoutContainerClasses.push(classes.NoNavbar);
  }
  if (staticPages.includes(location.pathname)) layoutContainerClasses.push(classes.StaticPage);

  if (isMobile) layoutContainerClasses.push(classes.MobileDevice);

  return (
    <div>
      {/* only show banner on logged in pages. Auth pages already have error message window. */}
      {appErrorMessage && isAuthenticated && !appErrorBannerHiddenPages.includes(location.pathname) && (
        <Banner title={appErrorTitle || 'System Error'} message={appErrorMessage} close={onClearAppError} className="AppErrorBanner" />
      )}
      <div className={layoutContainerClasses.join(' ')}>
        <div className={classes.PageContentContainer}>
          <LayoutNavBar
            handleNavLinkClick={handleNavLinkClick}
            showAccountDropdown={showAccountDropdown}
            setShowAccountDropdown={setShowAccountDropdown}
            toggleServerListModal={toggleServerListModal}
          />
          <main className={classes.Content}>{children}</main>
          <Footer show={!isMobile && showFooter} handleNavLinkClick={handleNavLinkClick} />
        </div>
        {renderedModal}
      </div>
    </div>
  );
};

export default Layout;
